import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Badge, Button, Card, Skeleton } from '@/ui/primitives'
import { FaraDrept, Grup } from './comune'

type Job = {
  service: string; timer: string | null; description: string
  state: string; substate: string; result: string; exit_code: string
  last_start: string; last_exit: string; running_since: string; restarts: string
  ok: boolean; next_run: string; timer_active: boolean | null
}

/** systemd da timpii in forma „Tue 2026-09-16 07:00:01 EEST"; ii scurtam, dar nu-i
 *  reinterpretam — ora afisata e cea a serverului, ca in jurnal. */
const scurt = (s: string) => {
  if (!s || s === 'n/a') return '—'
  const [, zi, ora] = s.split(' ')
  return zi && ora ? `${zi} ${ora.slice(0, 5)}` : s
}

export function SetariJoburi() {
  const qc = useQueryClient()
  const [deschis, setDeschis] = useState<string | null>(null)
  const [ore, setOre] = useState('')
  const [zile, setZile] = useState<'daily' | 'weekdays'>('daily')
  const [eroare, setEroare] = useState<string | null>(null)

  const lista = useQuery({
    queryKey: ['joburi'], retry: false,
    queryFn: () => api.get<{ jobs: Job[] }>('/system-jobs/'),
    refetchInterval: 30_000,
  })
  const jurnal = useQuery({
    queryKey: ['jurnal-job', deschis],
    enabled: Boolean(deschis),
    queryFn: () => api.get<{ lines: string[] }>('/system-jobs/log', { unit: deschis!, lines: 120 }),
  })

  const prinde = (e: unknown) => setEroare(e instanceof Error ? e.message : 'Comanda a esuat')
  const reia = () => qc.invalidateQueries({ queryKey: ['joburi'] })

  const actiune = useMutation({
    mutationFn: (v: { unit: string; action: string }) => api.post('/system-jobs/action', v),
    onSuccess: reia, onError: prinde,
  })
  const programeaza = useMutation({
    mutationFn: (v: { timer: string; times: string[]; days: string }) => api.post('/system-jobs/schedule', v),
    onSuccess: () => { setOre(''); reia() }, onError: prinde,
  })

  const faraDrept = (lista.error as ApiError | null)?.status === 403
  if (faraDrept) return <div className="px-8 py-6"><FaraDrept ce="Joburile automate" /></div>
  if (lista.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={8} /></Card></div>

  const joburi = lista.data?.jobs ?? []

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4">
        <p className="text-[13.5px] text-muted">
          Automatizarile aplicatiei, asa cum le vede serverul. Serviciile permanente (backend,
          programator) se vad, dar nu se opresc de aici.
        </p>
      </Card>

      {eroare && <Card className="p-3"><span className="text-[13px] text-bad">{eroare}</span></Card>}

      <div className="space-y-3">
        {joburi.map((j) => (
          <Card key={j.service} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2.5">
                  <Badge tone={j.ok ? 'good' : 'bad'}>{j.state}{j.substate ? ` / ${j.substate}` : ''}</Badge>
                  <span className="text-[14.5px] font-medium">{j.description}</span>
                </div>
                <p className="mt-1 text-[12.5px] text-muted">{j.service}{j.timer ? ` · ${j.timer}` : ''}</p>
                <p className="mt-1 text-[12.5px] text-muted">
                  Ultima pornire: {scurt(j.last_start)} · s-a incheiat: {scurt(j.last_exit)}
                  {j.exit_code ? ` · cod ${j.exit_code}` : ''}
                  {j.next_run ? ` · urmatoarea: ${scurt(j.next_run)}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {j.timer && (
                  <>
                    <Button variant="quiet" disabled={actiune.isPending}
                      onClick={() => { setEroare(null); actiune.mutate({ unit: j.service, action: 'run-now' }) }}>
                      Ruleaza acum
                    </Button>
                    <Button variant="quiet" disabled={actiune.isPending}
                      onClick={() => { setEroare(null); actiune.mutate({ unit: j.service, action: 'stop' }) }}>
                      Opreste
                    </Button>
                    <Button variant="quiet" disabled={actiune.isPending}
                      onClick={() => {
                        setEroare(null)
                        actiune.mutate({ unit: j.timer!, action: j.timer_active === false ? 'timer-on' : 'timer-off' })
                      }}>
                      {j.timer_active === false ? 'Porneste programarea' : 'Opreste programarea'}
                    </Button>
                  </>
                )}
                <Button variant="quiet" onClick={() => setDeschis(deschis === j.service ? null : j.service)}>
                  {deschis === j.service ? 'Ascunde jurnalul' : 'Jurnal'}
                </Button>
              </div>
            </div>

            {j.timer && (
              <div className="mt-3 flex flex-wrap items-end gap-2.5">
                <div>
                  <label htmlFor={`o-${j.timer}`} className="text-[12.5px] text-muted mb-1 block">
                    Ore de rulare (despartite prin virgula)
                  </label>
                  <input id={`o-${j.timer}`} value={deschis === j.service ? ore : ''}
                    onFocus={() => setDeschis(j.service)}
                    onChange={(e) => setOre(e.target.value)} placeholder="07:00, 12:00"
                    className="h-9 w-56 px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px] focus:border-accent outline-none" />
                </div>
                <select value={zile} onChange={(e) => setZile(e.target.value as 'daily' | 'weekdays')}
                  className="h-9 px-3 rounded-[var(--radius-control)] border border-line bg-surface text-[14px]">
                  <option value="daily">in fiecare zi</option>
                  <option value="weekdays">luni–vineri</option>
                </select>
                <Button variant="quiet" disabled={programeaza.isPending || !ore || deschis !== j.service}
                  onClick={() => {
                    setEroare(null)
                    programeaza.mutate({
                      timer: j.timer!,
                      times: ore.split(',').map((t) => t.trim()).filter(Boolean),
                      days: zile,
                    })
                  }}>
                  Schimba programarea
                </Button>
              </div>
            )}

            {deschis === j.service && (
              <pre className="mt-3 text-[12px] leading-[1.5] bg-page rounded-lg p-3 max-h-72 overflow-auto whitespace-pre-wrap">
                {jurnal.isPending ? 'Se citeste jurnalul…' : (jurnal.data?.lines?.join('\n') || 'Fara linii in jurnal.')}
              </pre>
            )}
          </Card>
        ))}
      </div>

      <Grup titlu="Cum se citeste">
        <p className="text-[13.5px] text-muted">
          „Ruleaza acum" porneste jobul o singura data, imediat. „Opreste programarea" lasa jobul
          instalat, dar nu-l mai declanseaza la ora lui — starea se vede in randul de mai sus.
        </p>
      </Grup>
    </div>
  )
}
